/**
 * Is there an in-app page behind the current one to go back to?
 *
 * Read from the router's own history index, which React Router stamps into
 * `history.state` and reads back for its navigation bookkeeping. `idx === 0` is
 * the first entry of this app session — a deep link, a fresh tab, or a reload
 * of a directly-opened URL — where popping would leave the app entirely.
 *
 * This replaces `window.history.length > 1`, which counts the WHOLE tab's
 * history: it is true when the previous entry belongs to another site, so a
 * deep-linked visitor's Back would take them off the app rather than to a
 * sensible page. It also survives remounts, unlike `useNavigationType()`, which
 * describes only the most recent navigation.
 *
 * Falls back to `false` when no index is present, so callers use their explicit
 * fallback path rather than guessing.
 *
 * The rules this belongs to — one screen, one history entry; returning is a pop,
 * never a push — are in docs/FeatureDesigns/assignments-tests/solve-screen-history-plan.md.
 */
export function hasHistoryBehind(historyState: unknown = typeof window !== 'undefined' ? window.history.state : null): boolean {
    const idx = (historyState as { idx?: number } | null)?.idx;
    return typeof idx === 'number' && idx > 0;
}
